import React from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Box, CircularProgress } from "@mui/material";
import InputField from "@components/common/InputField";
import CustomSelect from "@components/common/Select/Select";
import type { CreateEquipmentRequest, Equipment } from "../types/types";
import Button from "@components/common/Button/Button";
import { addEquipmentSchema, equipmentTypeOptions } from "../schema/add.equipment.schema";
import { useTranslation } from "react-i18next";

const EQUIPMENT_TYPE_OPTIONS = [
	{ value: "", label: "Select Equipment Type", isPlaceholder: true },
	{ value: "core", label: "Core" },
	{ value: "consumable", label: "Consumable" },
	{ value: "accessory", label: "Accessory" },
];

type AddEquipmentFormData = z.infer<typeof addEquipmentSchema>;

interface AddEquipmentFormProps {
	onSubmit: (data: CreateEquipmentRequest) => void;
	onCancel: () => void;
	isSubmitting: boolean;
	initialValues?: Equipment | null;
	isEdit?: boolean;
	isView?: boolean;
}

const AddEquipmentForm: React.FC<AddEquipmentFormProps> = ({
	onSubmit,
	onCancel,
	isSubmitting,
	initialValues,
	isEdit = false,
	isView = false,
}) => {
	const { t } = useTranslation();
	const isReadOnly = isView;
	const {
		control,
		handleSubmit,
		formState: { isValid },
		reset,
		watch,
		setValue,
	} = useForm<AddEquipmentFormData>({
		resolver: zodResolver(addEquipmentSchema),
		mode: "onChange",
		defaultValues: initialValues
			? {
					label: initialValues.label,
					equipmentType: initialValues.equipmentType || undefined,
					description: initialValues.description || "",
				}
			: { label: "", equipmentType: undefined, description: "" },
	});

	const labelValue = watch("label");
	const equipmentTypeValue = watch("equipmentType");
	const isFormValid =
		labelValue &&
		labelValue.trim().length > 0 &&
		equipmentTypeValue &&
		equipmentTypeOptions.includes(equipmentTypeValue) &&
		isValid;

	const handleFormSubmit = (data: AddEquipmentFormData) => {
		if (isReadOnly) return;
		const submitData: CreateEquipmentRequest = {
			label: data?.label?.trim(),
			equipmentType: data?.equipmentType,
			description: data.description?.trim() || undefined,
		};

		onSubmit(submitData);
	};

	const handleCancel = () => {
		if (!isSubmitting) {
			reset();
			onCancel();
		}
	};

	const onFormSubmit = handleSubmit(handleFormSubmit);
	const fieldsDisabled = isSubmitting || isReadOnly;

	return (
		<form onSubmit={onFormSubmit} className="space-y-4">
			<Controller
				name="label"
				control={control}
				render={({ field, fieldState }) => (
					<InputField
						id="equipment-label"
						label={t("feature.courseBuilder.equipments.labels.equipmentName")}
						type="text"
						placeholder={
							isEdit
								? t("feature.courseBuilder.equipments.placeholders.updateEquipmentName")
								: t("feature.courseBuilder.equipments.placeholders.equipmentName")
						}
						error={!!fieldState?.error}
						helperText={fieldState?.error?.message}
						disabled={fieldsDisabled}
						required={!isReadOnly}
						{...field}
						value={field?.value || ""}
					/>
				)}
			/>

			<Controller
				name="equipmentType"
				control={control}
				render={({ field, fieldState }) => (
					<CustomSelect
						id="equipment-type"
						label={t("feature.courseBuilder.equipments.labels.equipmentType")}
						required={!isReadOnly}
						options={EQUIPMENT_TYPE_OPTIONS}
						selected={field.value || ""}
						setSelected={value => {
							if (isReadOnly) return;
							setValue("equipmentType", value as (typeof equipmentTypeOptions)[number], {
								shouldValidate: true,
							});
						}}
						placeholder="Select Equipment Type"
						error={!!fieldState?.error}
						helperText={fieldState?.error?.message}
						disabled={fieldsDisabled}
					/>
				)}
			/>

			<Controller
				name="description"
				control={control}
				render={({ field, fieldState }) => (
					<InputField
						id="equipment-description"
						label={t("feature.courseBuilder.equipments.labels.description")}
						type="text"
						placeholder={t("feature.courseBuilder.equipments.placeholders.description")}
						multiline
						rows={4}
						error={!!fieldState.error}
						helperText={fieldState.error?.message}
						disabled={fieldsDisabled}
						{...field}
						value={field.value || ""}
					/>
				)}
			/>

			{!isEdit && !isView && (
				<div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
					<strong>{t("common.note")}</strong> {t("feature.courseBuilder.equipments.note")}
				</div>
			)}

			<Box className="flex gap-3 justify-end pt-4">
				{isReadOnly ? (
					<Button type="button" variant="primary" onClick={handleCancel} className="min-w-[100px]">
						{t("psdaCommon.common.buttons.close")}
					</Button>
				) : (
					<>
						<Button
							type="button"
							variant="secondary"
							onClick={handleCancel}
							disabled={isSubmitting}
							className="min-w-[100px]">
							{t("psdaCommon.common.buttons.cancel")}
						</Button>
						<Button
							type="submit"
							variant="primary"
							disabled={!isFormValid || isSubmitting}
							className="min-w-[100px]">
							{isSubmitting ? (
								<CircularProgress size={20} color="inherit" />
							) : isEdit ? (
								t("feature.courseBuilder.equipments.buttons.updateEquipment")
							) : (
								t("feature.courseBuilder.equipments.buttons.addEquipment")
							)}
						</Button>
					</>
				)}
			</Box>
		</form>
	);
};

export default AddEquipmentForm;
